import React from 'react';
import { Sparkles, Globe, Heart, Users, Leaf, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Sparkles,
      title: 'Authentic Exploration',
      description: 'We foster genuine connections with cultures and communities, ensuring every journey is meaningful and respectful.'
    },
    {
      icon: Globe,
      title: 'Seamless Journeys',
      description: 'From planning to execution, we handle every detail to ensure your adventure is stress-free and unforgettable.'
    },
    {
      icon: TrendingUp,
      title: 'Lifelong Discovery',
      description: 'We inspire a passion for exploration that lasts a lifetime, helping you discover the world and yourself.'
    },
    {
      icon: Leaf,
      title: 'Sustainable Travel',
      description: 'Every experience we create is deeply respectful of diverse cultures and environments, ensuring eco-conscious tourism.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-32 pt-48 overflow-hidden">
        {/* Logo Icon Watermark */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_conscious-escape/artifacts/o5c63p3f_customcolor_icon_transparent_background.png" 
            alt="" 
            className="w-[600px] h-auto"
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            ABOUT <span className="text-[#84bc00]">EXCITING.TRAVEL</span>
          </h1>
          <p className="text-2xl text-gray-300 font-semibold max-w-3xl mx-auto">
            Your premier gateway to sustainable adventures, offering thrilling journeys designed for conscious explorers.
          </p>
        </div>
      </div>

      {/* Featured Image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20">
        <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
          <img 
            src="https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?w=1200&h=600&fit=crop" 
            alt="Exciting Travel Experience" 
            className="w-full h-96 object-cover"
          />
        </div>
      </div>

      {/* Our Story */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-black text-black mb-8">
            OUR <span className="text-[#ffb84d]">STORY</span>
          </h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p className="font-medium">
              We believe travel should be an <strong>exhilarating, guilt-free experience</strong> — truly making us your passport to bucket-list adventures without breaking the planet.
            </p>
            <p>
              At Exciting.Travel, we're passionate about proving that <strong>popular doesn't have to mean destructive</strong>. Every adventure we facilitate is built on eco-consciousness, energy, and a dedication to making incredible destinations accessible for conscious travelers.
            </p>
            <p>
              From vibrant beach clubs in Costa Rica to cultural immersions in Cuba, we curate experiences that resonate with your spirit of adventure while respecting local communities and the environment.
            </p>
            <p className="text-xl font-bold text-[#84bc00]">
              All the thrills. None of the guilt.
            </p>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-black mb-6">
              OUR CORE <span className="text-[#84bc00]">VALUES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide every adventure we create
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 border-transparent hover:border-[#84bc00]"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#84bc00] to-[#6a9600] rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform duration-300">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-black text-black mb-4 group-hover:text-[#84bc00] transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Our Mission */}
      <div className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#84bc00] to-[#6a9600] rounded-3xl p-12 md:p-16 text-center shadow-2xl">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-900 rounded-full mb-8">
              <Heart className="text-[#84bc00]" size={40} />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
              OUR MISSION
            </h2>
            <p className="text-xl text-gray-900 leading-relaxed max-w-3xl mx-auto font-semibold">
              To craft <strong>unforgettable, sustainable travel experiences</strong> that prove you can hit every bucket-list destination without wrecking it. We're committed to making thrilling adventures accessible for conscious explorers who want the energy of popular destinations with the peace of mind of eco-certified travel.
            </p>
            <div className="mt-10 flex items-center justify-center space-x-3">
              <Users size={24} className="text-gray-900" />
              <span className="text-gray-900 font-black text-2xl">Ages: All Conscious Travelers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg font-semibold text-gray-300 mb-4">
            exciting.travel is a <span className="text-[#84bc00] font-black">Thom Kriner, LLC</span> company
          </p>
          <p className="text-base text-gray-400">
            Specializing in Latin America & Caribbean sustainable travel experiences
          </p>
          <div className="mt-8 flex items-center justify-center space-x-8">
            <div className="text-center">
              <div className="text-3xl font-black text-[#84bc00]">100%</div>
              <div className="text-sm text-gray-400">ECO-CERTIFIED</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-[#ffb84d]">15+</div>
              <div className="text-sm text-gray-400">DESTINATIONS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-[#0ea5a5]">5K+</div>
              <div className="text-sm text-gray-400">HAPPY TRAVELERS</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
